import React, { Component } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, ExternalLink } from 'lucide-react';
import { logger } from '../../utils/logger';
import { isMobile } from 'react-device-detect';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
  autoRetryEnabled: boolean;
  showDetails: boolean;
  isMobile: boolean;
}

class GlobalErrorBoundary extends Component<Props, State> {
  private retryTimeout: NodeJS.Timeout | null = null;
  private maxRetries = 3;
  private autoRetryDelay = 3000; // 3 seconds

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
      autoRetryEnabled: true,
      showDetails: false,
      isMobile: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('🚨 Global Error Boundary caught an error:', error);
    logger.error('Error Info:', errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error to external service (if needed)
    this.logErrorToService(error, errorInfo);

    // Auto retry if enabled and within retry limit
    if (this.state.autoRetryEnabled && this.state.retryCount < this.maxRetries) {
      this.scheduleAutoRetry();
    }
  }

  componentDidMount() {
    try {
      const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
      const smallScreen = window.matchMedia?.('(max-width: 640px)').matches;
      const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      );
      const isMobile = Boolean(coarsePointer || smallScreen || uaMobile);

      if (isMobile) {
        this.setState({ isMobile: true, autoRetryEnabled: false });
      } else {
        this.setState({ isMobile: false });
      }
    } catch {
      // No-op
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  private logErrorToService = (error: Error, errorInfo: React.ErrorInfo) => {
    try {
      // Log to console for development
      console.group('🐛 Error Details');
      logger.error('Error:', error.message);
      logger.error('Stack:', error.stack);
      logger.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();

      // In production, you might want to send this to an error tracking service
      // Example: Sentry, LogRocket, etc.
      if (process.env.NODE_ENV === 'production') {
        // window.errorTracker?.captureException(error, { extra: errorInfo });
      }
    } catch (loggingError) {
      logger.error('Failed to log error:', loggingError);
    }
  };

  private scheduleAutoRetry = () => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    this.setState({ isRetrying: true });

    this.retryTimeout = setTimeout(() => {
      this.handleRetry(true);
    }, this.autoRetryDelay);
  };

  private handleRetry = (isAutoRetry = false) => {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }

    this.setState((prevState) => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: isAutoRetry ? prevState.retryCount + 1 : 0,
      isRetrying: false,
    }));

    logger.log(`🔄 ${isAutoRetry ? 'Auto' : 'Manual'} retry attempt`);
  };

  private handleReload = () => {
    try {
      window.location.reload();
    } catch (error) {
      logger.error('Failed to reload page:', error);
      // Fallback: try to navigate to home
      window.location.href = '/';
    }
  };

  private handleGoHome = () => {
    try {
      window.location.href = '/';
    } catch (error) {
      logger.error('Failed to navigate home:', error);
      this.handleReload();
    }
  };

  private toggleAutoRetry = () => {
    this.setState((prevState) => {
      const newAutoRetryEnabled = !prevState.autoRetryEnabled;

      if (!newAutoRetryEnabled && this.retryTimeout) {
        clearTimeout(this.retryTimeout);
        this.setState({ isRetrying: false });
      }

      return { autoRetryEnabled: newAutoRetryEnabled };
    });
  };

  private toggleDetails = () => {
    this.setState((prevState) => ({ showDetails: !prevState.showDetails }));
  };

  private reportBug = () => {
    const { error, errorInfo } = this.state;
    const errorReport = {
      message: error?.message || 'Unknown error',
      stack: error?.stack || 'No stack trace',
      componentStack: errorInfo?.componentStack || 'No component stack',
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    // Create mailto link with error details
    const subject = encodeURIComponent('Bug Report: Application Error');
    const body = encodeURIComponent(
      `Error Report:\n\n` +
        `Message: ${errorReport.message}\n` +
        `URL: ${errorReport.url}\n` +
        `Timestamp: ${errorReport.timestamp}\n` +
        `User Agent: ${errorReport.userAgent}\n\n` +
        `Stack Trace:\n${errorReport.stack}\n\n` +
        `Component Stack:\n${errorReport.componentStack}`,
    );

    window.open(`mailto:support@myeztopup.com?subject=${subject}&body=${body}`);
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className={`${
            this.state.isMobile
              ? 'min-h-[100svh] overflow-y-auto'
              : 'h-dvh md:min-h-screen overflow-y-auto'
          } bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4`}
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-4 sm:p-8 relative overflow-hidden">
            {/* Background Pattern */}
            <div className={`absolute inset-0 ${this.state.isMobile ? 'opacity-0' : 'opacity-5'}`}>
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-orange-500 transform rotate-12 scale-150"></div>
            </div>

            {/* Content */}
            <div className="relative z-10">
              {/* Error Icon */}
              <div className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>

              {/* Title */}
              <h1 className="text-3xl font-bold text-gray-900 text-center mb-4">
                Oops! Something went wrong
              </h1>

              {/* Error Message */}
              <p className="text-gray-600 text-center mb-6 text-lg">
                {this.state.error?.message ||
                  "An unexpected error occurred. Don't worry, we're on it!"}
              </p>

              {/* Auto Retry Status */}
              {this.state.isRetrying &&
                this.state.retryCount < this.maxRetries &&
                this.state.autoRetryEnabled && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center space-x-3">
                      <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                      <div>
                        <p className="text-blue-800 font-medium">
                          Auto-retrying... (Attempt {this.state.retryCount + 1}/{this.maxRetries})
                        </p>
                        <p className="text-blue-600 text-sm">
                          The app will automatically retry in a few seconds
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Retry Limit Reached */}
              {this.state.retryCount >= this.maxRetries && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <p className="text-orange-800 font-medium">
                    ⚠️ Maximum auto-retry attempts reached
                  </p>
                  <p className="text-orange-600 text-sm">
                    Please try manual recovery options below
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {!isMobile && (
                  <button
                    onClick={() => this.handleRetry(false)}
                    className="flex items-center justify-center space-x-2 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                  >
                    <RefreshCw className="w-5 h-5" />
                    <span>Try Again</span>
                  </button>
                )}

                <button
                  onClick={this.handleReload}
                  className="flex items-center justify-center space-x-2 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <RefreshCw className="w-5 h-5" />
                  <span>Reload Page</span>
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="flex items-center justify-center space-x-2 bg-gray-600 text-white py-3 px-6 rounded-lg hover:bg-gray-700 transition-colors font-medium"
                >
                  <Home className="w-5 h-5" />
                  <span>Go Home</span>
                </button>

                <button
                  onClick={this.reportBug}
                  className="flex items-center justify-center space-x-2 bg-orange-600 text-white py-3 px-6 rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                  <Bug className="w-5 h-5" />
                  <span>Report Bug</span>
                </button>
              </div>

              {/* Settings */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={this.state.autoRetryEnabled}
                      onChange={this.toggleAutoRetry}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-700 font-medium">Enable Auto-Retry</span>
                  </label>

                  <button
                    onClick={this.toggleDetails}
                    className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                  >
                    {this.state.showDetails ? 'Hide' : 'Show'} Error Details
                  </button>
                </div>

                {/* Error Details */}
                {this.state.showDetails && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-2">Error Details:</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Message:</span>
                        <p className="text-gray-600 font-mono bg-white p-2 rounded border mt-1">
                          {this.state.error?.message || 'No error message'}
                        </p>
                      </div>
                      {this.state.error?.stack && (
                        <div>
                          <span className="font-medium text-gray-700">Stack Trace:</span>
                          <pre className="text-gray-600 font-mono bg-white p-2 rounded border mt-1 overflow-x-auto text-xs">
                            {this.state.error.stack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Help Links */}
              <div className="mt-6 text-center">
                <p className="text-gray-500 text-sm mb-2">Need help? Contact our support team</p>
                <div className="flex justify-center space-x-4">
                  <a
                    href="mailto:support@myeztopup.com"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Email Support
                  </a>
                  <a
                    href="https://status.myeztopup.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1"
                  >
                    <span>Status Page</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default GlobalErrorBoundary;
